import * as Api from '@ViewModels';
import { getDefaultBulkMessagingBodyEditorState } from '../../../../models/UiUtils';
import { ComposeFollowUpEmailViewModel } from '../../../../viewmodels/AppViewModels';

/**
 * Builds the composer for a follow up on `campaign`. Passing `groupStats` follows up on every campaign in the
 * campaign's send-on-behalf group instead.
 */
export function createFollowUpComposer(
	userSession: Api.UserSessionContext,
	campaign: Api.ICampaign,
	groupStats?: Api.ICampaignGroup
) {
	// The recipient picker counts from the follow-up source, so a group follow up counts across the whole group.
	const campaignVm = new Api.CampaignViewModel(
		userSession,
		groupStats ? { ...campaign, ...groupCountsFrom(groupStats) } : campaign
	);
	const composer = new ComposeFollowUpEmailViewModel<Api.IEmailMessageFollowUpOptions, Api.CampaignViewModel>(
		userSession,
		campaignVm
	);

	composer.emailMessage = new Api.EmailMessageViewModel<File, Api.ISendEmailResponse, Api.IEmailMessageFollowUpOptions>(
		userSession
	);
	composer.allowSenderSelection = false;
	composer.emailMessage.content = getDefaultBulkMessagingBodyEditorState().getRawRichTextContent();
	if (campaign.sendToHousehold) {
		composer.emailMessage.groupByHousehold = true;
	}
	if (campaign.id) {
		composer.emailMessage.options.followUp = {
			_type: 'EmailFollowUpOptions',
			bulkEmailMessageId: campaign.id,
			...(groupStats ? { applyToGroup: true } : {}),
		};
	}
	if (groupStats) {
		// The server fills in each copy's sender; SelectedUser keeps the on-behalf scheduling rules.
		composer.emailMessage.options.sendEmailFrom = Api.SendEmailFrom.SelectedUser;
		return composer;
	}
	// Per-recipient sends split into one campaign per owner, so the creator is the one original sender.
	const originalCreator = campaign.creator;
	if (originalCreator?.id && originalCreator.id !== userSession.user.id && userSession.canSendOnBehalf) {
		composer.emailMessage.options.sendEmailFrom = Api.SendEmailFrom.SelectedUser;

		composer.emailMessage.setSendEmailFromUser({
			id: originalCreator.id,
			firstName: originalCreator.firstName,
			lastName: originalCreator.lastName,
			email: originalCreator.email,
			primaryEmail: originalCreator.primaryEmail,
		} as Pick<Api.IUser, 'id' | 'firstName' | 'lastName' | 'email' | 'primaryEmail'> as Api.IUser);
	}
	return composer;
}

function groupCountsFrom(group: Api.ICampaignGroup): Partial<Api.ICampaign> {
	const counts: Partial<Api.ICampaign> = {
		bouncedCount: group.bouncedCount,
		distinctClickCount: group.distinctClickCount,
		failedCount: group.failedCount,
		hasClickTargets: group.hasClickTargets,
		noEmailAddressCount: group.noEmailAddressCount,
		openCount: group.openCount,
		replyCount: group.replyCount,
		sentSuccessfullyCount: group.sentSuccessfullyCount,
		totalContacts: group.totalContacts,
		totalEmails: group.totalEmails,
	};
	// A field missing from the response must not blank out the campaign's own value.
	return Object.fromEntries(Object.entries(counts).filter(([, value]) => value !== undefined));
}
